import { useCallback, useMemo } from 'react';
import {
  FiltersPanel,
  type FiltersPanelStatusOption,
  type FiltersPanelSegmentedOption,
} from '../shared/FiltersPanel';
import type {
  TriggerHistoryFilters,
  TriggerHistoryStatus,
} from '@/types/monitoring';
import { isTriggerHistoryStatusFilterActive } from '@/types/monitoring';

type TriggerHistoryFiltersProps = {
  filters: TriggerHistoryFilters;
  onStatusToggle: (status: TriggerHistoryStatus) => void;
  onSearchChange: (value: string) => void;
  onTimeRangeChange: (preset: TimeRangePreset) => void;
  onClear: () => void;
  /** Currently selected time-range preset (controlled). */
  timeRangePreset: TimeRangePreset;
};

export type TimeRangePreset = 'all' | '1h' | '6h' | '24h' | '7d' | '30d';

const STATUS_OPTIONS: ReadonlyArray<
  FiltersPanelStatusOption<TriggerHistoryStatus>
> = [
  { value: 'succeeded', label: 'Succeeded', tone: 'succeeded' },
  { value: 'failed', label: 'Failed', tone: 'failed' },
  { value: 'cancelled', label: 'Cancelled', tone: 'cancelled' },
  { value: 'skipped', label: 'Skipped', tone: 'skipped' },
  { value: 'running', label: 'Running', tone: 'running' },
];

const TIME_RANGE_OPTIONS: ReadonlyArray<FiltersPanelSegmentedOption<TimeRangePreset>> = [
  { value: 'all', label: 'All' },
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];

export const TriggerHistoryFiltersBar = ({
  filters,
  onStatusToggle,
  onSearchChange,
  onTimeRangeChange,
  onClear,
  timeRangePreset,
}: TriggerHistoryFiltersProps) => {
  const activeCount = useMemo(() => {
    let n = 0;
    if (isTriggerHistoryStatusFilterActive(filters.statuses)) n += 1;
    if (filters.clientTrackingId) n += 1;
    if (timeRangePreset !== 'all' || filters.startTime?.from) n += 1;
    return n;
  }, [filters.statuses, filters.clientTrackingId, filters.startTime?.from, timeRangePreset]);

  const handleStatusToggle = useCallback(
    (value: TriggerHistoryStatus) => onStatusToggle(value),
    [onStatusToggle]
  );

  return (
    <FiltersPanel<TriggerHistoryStatus, string, TimeRangePreset>
      title="Filters"
      idPrefix="trigger-history-filters"
      activeFilterCount={activeCount}
      onClear={onClear}
      search={{
        label: 'Search',
        placeholder: 'Search by tracking ID',
        value: filters.clientTrackingId ?? '',
        onChange: onSearchChange,
      }}
      status={{
        label: 'Status',
        options: STATUS_OPTIONS,
        selected: filters.statuses,
        onToggle: handleStatusToggle,
      }}
      timeRange={{
        label: 'Time Range',
        options: TIME_RANGE_OPTIONS,
        selected: timeRangePreset,
        onChange: onTimeRangeChange,
      }}
    />
  );
};
